import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  Text,
  Button,
  Spinner,
  Heading,
  Icon,
  Center,
  useToast,
  Link,
} from "@chakra-ui/react";
import GroupCard from "./GroupCard";
import { FaUsers, FaExclamationTriangle, FaStore, FaMotorcycle } from "react-icons/fa";
import { useDispatchDashboardStore, getActiveSelectionGroups, isConsolidatedSelectionGroup } from "../store/useDispatchDashboardStore";
import { useCustomerFacets } from "../hooks/useCustomerFacets";
import { useSupplierFacets } from "../hooks/useSupplierFacets";
import { useAllSupplierFacets } from "../hooks/useAllSupplierFacets";
import { useRiderFacets } from "../hooks/useRiderFacets";
import { usePorterQuotes } from "../hooks/usePorterQuotes";
import { useShipmentCreation } from "../hooks/useShipmentCreation";
import { useOrderGrouping } from "../hooks/useOrderGrouping";
import { getDispatchOrdersForSupplier } from "../services";
import QuoteBar from "./QuoteBar";
import { DeliveryModes, DispatchMode } from "../../../types";
import { RiderDetails, GroupingMode, GroupingType } from "../types";
import { SelectedRiderDetails } from "./QuoteBar";
import { getDispatchCustomerFacets } from "../services";
import { calculateStatsFromCustomers } from "../../../utils/orderUtils";
import {
  calculateSuggestedVehicleType,
  FASTEST_CPT_SLAB_LEVEL,
} from "../utils";

interface OrdersTableProps {
  customerFilter?: string;
  isApiConfigReady?: boolean;
  riders?: RiderDetails[];
  ridersLoading?: boolean;
  onStatsUpdate?: (stats: {
    totalCustomers: number;
    totalOrders: number;
    totalValues: number;
    selectedOrders: number;
    urgentOrders: number;
    hldOrders?: number;
  }) => void;
  onRefetchReady?: (refetch: () => Promise<void>) => void;
  onLoadingChange?: (loading: boolean) => void;
  onDataUpdatedAtChange?: (timestamp: number) => void;
  collapseSignal?: number;
  maskDeliveryContactDetails?: boolean;
  userName?: string;
  activeTab?: string;
  enableDistanceTabs?: boolean;
  grouping?: GroupingType;
  allowMultiCustomerShipment?: boolean;
  dashboardMode?: "r2d" | "dispatched";
}

export default function OrdersTable({
  customerFilter = "all",
  isApiConfigReady = false,
  riders,
  ridersLoading = false,
  onStatsUpdate,
  onRefetchReady,
  onLoadingChange,
  onDataUpdatedAtChange,
  collapseSignal,
  maskDeliveryContactDetails,
  userName,
  activeTab = FASTEST_CPT_SLAB_LEVEL,
  enableDistanceTabs = false,
  grouping = GroupingMode.SUPPLIER,
  allowMultiCustomerShipment = false,
  dashboardMode = "r2d",
}: OrdersTableProps) {
  const toast = useToast();
  const isCustomerGrouping = grouping === GroupingMode.CUSTOMER;
  const isSupplierGrouping = grouping === GroupingMode.SUPPLIER;
  const isRiderGrouping = grouping === GroupingMode.RIDER;
  const isDispatchedDashboard = dashboardMode === "dispatched";

  const {
    facets: customerFacets,
    loading: customerLoading,
    filteredCustomers,
    refetch: refetchCustomers,
    dataUpdatedAt: customerDataUpdatedAt,
  } = useCustomerFacets(
    customerFilter,
    isApiConfigReady && isCustomerGrouping,
    enableDistanceTabs ? activeTab : undefined,
  );

  const {
    facets: supplierFacets,
    loading: supplierLoading,
    filteredSuppliers,
    refetch: refetchSuppliers,
    dataUpdatedAt: supplierDataUpdatedAt,
  } = useAllSupplierFacets(
    "all",
    isApiConfigReady && isSupplierGrouping,
    enableDistanceTabs ? activeTab : undefined,
  );

  const {
    facets: riderFacets,
    loading: riderLoading,
    filteredRiders,
    refetch: refetchRiders,
    dataUpdatedAt: riderDataUpdatedAt,
  } = useRiderFacets(
    isApiConfigReady && isRiderGrouping,
    enableDistanceTabs ? activeTab : undefined,
  );

  const facets = isCustomerGrouping
    ? customerFacets
    : isSupplierGrouping
    ? supplierFacets
    : riderFacets;
  const loading = isCustomerGrouping
    ? customerLoading
    : isSupplierGrouping
    ? supplierLoading
    : riderLoading;
  const refetch = isCustomerGrouping
    ? refetchCustomers
    : isSupplierGrouping
    ? refetchSuppliers
    : refetchRiders;
  const dataUpdatedAt = isCustomerGrouping
    ? customerDataUpdatedAt
    : isSupplierGrouping
    ? supplierDataUpdatedAt
    : riderDataUpdatedAt;

  const [hldStats, setHldStats] = useState<{
    totalOrders: number;
    totalCustomers: number;
    totalValues: number;
    urgentOrders: number;
  } | null>(null);
  const [hldStatsLoading, setHldStatsLoading] = useState(false);
  const [totalStats, setTotalStats] = useState<{
    totalOrders: number;
    totalCustomers: number;
    totalValues: number;
    urgentOrders: number;
  } | null>(null);
  const [totalStatsLoading, setTotalStatsLoading] = useState(false);
  const totalStatsFetchingRef = useRef(false);
  const hldStatsFetchingRef = useRef(false);
  const { findSupplierByName } = useSupplierFacets();
  const { fetchPorterQuote, createPorterQuotePayload } = usePorterQuotes();
  const { createShipmentAndBookPorter, createShipmentForOrders, dispatchOrdersOnly } =
    useShipmentCreation();
  const { groupedCustomers, getTotalStats, getCustomerRecordById } =
    useOrderGrouping(filteredCustomers);

  interface SupplierFacet {
    _id: string;
    count: number;
    businessName?: string;
    name?: string;
    totalInvoiceAmount?: number;
    totalOrderItemsQuantity?: number;
    totalItemsCount?: number;
    urgentOrdersCount?: number;
    minETAdate?: string;
    supplierAddress?: {
      addressLine1: string;
      addressLine2?: string;
      city: string;
      state: string;
      pinCode?: string;
      country?: string;
      location?: {
        latitude: number;
        longitude: number;
      };
    };
    [key: string]: unknown;
  }

  interface SupplierGroup {
    facet: SupplierFacet;
    supplierId: string;
    supplierName: string;
  }

  const groupedSuppliers = useMemo((): Record<string, SupplierGroup> => {
    return (filteredSuppliers as SupplierFacet[]).reduce(
      (acc: Record<string, SupplierGroup>, supplier: SupplierFacet) => {
        const key = supplier._id;
        acc[key] = {
          facet: supplier,
          supplierId: supplier._id,
          supplierName:
            supplier.businessName || supplier.name || "Unknown Supplier",
        };
        return acc;
      },
      {},
    );
  }, [filteredSuppliers]);

  interface RiderFacet {
    _id: string;
    count: number;
    name?: string;
    phone?: string;
    vehicleNumber?: string;
    totalInvoiceAmount?: number;
    totalOrderItemsQuantity?: number;
    totalItemsCount?: number;
    urgentOrdersCount?: number;
    minETAdate?: string;
    [key: string]: unknown;
  }

  interface RiderGroup {
    facet: RiderFacet;
    riderId: string;
    riderName: string;
  }

  const groupedRiders = useMemo((): Record<string, RiderGroup> => {
    return (filteredRiders as RiderFacet[]).reduce(
      (acc: Record<string, RiderGroup>, rider: RiderFacet) => {
        const key = rider._id;
        acc[key] = {
          facet: rider,
          riderId: rider._id,
          riderName: rider.name || "Unassigned",
        };
        return acc;
      },
      {},
    );
  }, [filteredRiders]);

  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(
    new Set(),
  );
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(
    new Set(),
  );
  const [expandedRiders, setExpandedRiders] = useState<Set<string>>(
    new Set(),
  );
  const [quoteCardsExpanded, setQuoteCardsExpanded] = useState(true);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);

  const {
    selectedOrders,
    selectionsByGroup,
    addSelectedOrder,
    removeSelectedOrder,
    clearGroup,
    updateGroupQuote,
    setGroupLoading,
  } = useDispatchDashboardStore();

  const activeGroups = useMemo(
    () => getActiveSelectionGroups(selectionsByGroup, allowMultiCustomerShipment),
    [selectionsByGroup, allowMultiCustomerShipment],
  );
  const activeGroupKeys = useMemo(
    () => new Set(activeGroups.map(([groupKey]) => groupKey)),
    [activeGroups],
  );

  const isRefreshing = loading || isRefreshingAll;

  useEffect(() => {
    onLoadingChange?.(isRefreshing);
  }, [isRefreshing, onLoadingChange]);

  useEffect(() => {
    if (dataUpdatedAt) {
      onDataUpdatedAtChange?.(dataUpdatedAt);
    }
  }, [dataUpdatedAt, onDataUpdatedAtChange]);

  useEffect(() => {
    if (collapseSignal && collapseSignal > 0) {
      setExpandedCustomers(new Set());
      setExpandedSuppliers(new Set());
      setExpandedRiders(new Set());
    }
  }, [collapseSignal]);

  const onBookPorter = async (groupKey: string) => {
    const group = selectionsByGroup[groupKey];
    if (!group.selectedOrders.length) {
      toast({
        title: "No Orders Selected",
        description: "Please select orders to create shipment",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (
      !group.porterQuote?.vehicles ||
      group.porterQuote.vehicles.length === 0
    ) {
      toast({
        title: "No Quote Available",
        description: "Please wait for the quote to load",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      const deliveryMode =
        getGroupDeliveryMode(groupKey) ?? DeliveryModes.DOOR_DELIVERY;
      // R2D: DISPATCH + shipment + book. Dispatch: shipment + book only.
      const result = await createShipmentAndBookPorter(
        group.selectedOrders,
        group.orderIdMap || {},
        deliveryMode,
        DispatchMode.SYSTEM_PORTER,
        maskDeliveryContactDetails,
        userName,
        isDispatchedDashboard,
      );

      toast({
        title: "Porter Booked Successfully",
        description: (
          <Box>
            <Text fontSize="sm">Shipment ID: {result.shipment.shipmentId}</Text>
            <Text fontSize="sm">Order ID: {result.porter.order_id}</Text>
            <Text fontSize="sm">
              Tracking:{" "}
              <Link
                href={result.porter.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                color="blue.100"
                textDecoration="underline"
                _hover={{ color: "blue.200" }}
              >
                {result.porter.tracking_url}
              </Link>
            </Text>
          </Box>
        ),
        status: "success",
        duration: 10000,
        isClosable: true,
        position: "top",
      });

      clearGroup(groupKey);
      await refetchAllStats();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const onSelfDispatch = async (
    groupKey: string,
    rider?: SelectedRiderDetails,
  ) => {
    const group = selectionsByGroup[groupKey];
    if (!group.selectedOrders.length) {
      toast({
        title: "No Orders Selected",
        description: isDispatchedDashboard
          ? "Please select orders to create shipment"
          : "Please select orders to self dispatch",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      // R2D dashboard: mark DISPATCHED only (no shipment).
      if (!isDispatchedDashboard) {
        await dispatchOrdersOnly({
          orderNumbers: group.selectedOrders,
          orderIds: group.orderIdMap || {},
          rider,
        });

        toast({
          title: "Orders Dispatched Successfully",
          description: (
            <Box>
              <Text fontSize="sm">
                Orders moved to Dispatched: {group.selectedOrders.join(", ")}
              </Text>
            </Box>
          ),
          status: "success",
          duration: 8000,
          isClosable: true,
          position: "top",
        });

        clearGroup(groupKey);
        await refetchAllStats();
        return;
      }

      // Dispatch dashboard: create shipment (backend transitions to SHIPPED).
      // Rider was already captured on R2D; reuse from order meta when dialog was skipped.
      let riderFromOrders: SelectedRiderDetails | undefined = rider;
      if (!riderFromOrders && group.orderDataMap) {
        const riderKey = (dr: {
          name?: string;
          phone?: string;
          vehicleNumber?: string;
          trackingUrl?: string;
        }) =>
          [
            dr.name || "",
            dr.phone || "",
            dr.vehicleNumber || "",
            dr.trackingUrl || "",
          ].join("|");

        const ridersOnOrders = group.selectedOrders
          .map((orderNo) => group.orderDataMap[orderNo]?.dispatchRider)
          .filter(
            (dr): dr is NonNullable<typeof dr> =>
              !!(dr && (dr.name || dr.phone)),
          );

        if (ridersOnOrders.length > 0) {
          const uniqueKeys = new Set(ridersOnOrders.map(riderKey));
          if (uniqueKeys.size > 1) {
            toast({
              title: "Conflicting Riders",
              description:
                "Selected orders were dispatched with different riders. Select orders for one rider only, or create separate shipments.",
              status: "error",
              duration: 8000,
              isClosable: true,
              position: "top",
            });
            return;
          }

          const dr = ridersOnOrders[0];
          riderFromOrders = {
            name: dr.name || "",
            phone: dr.phone || "",
            vehicleNumber: dr.vehicleNumber || "",
            trackingUrl: dr.trackingUrl || "",
          };
        }
      }

      const deliveryMode = getGroupDeliveryMode(groupKey);
      const shipmentData = await createShipmentForOrders({
        orderNumbers: group.selectedOrders,
        orderIds: group.orderIdMap || {},
        deliveryMode,
        dispatchMode: DispatchMode.SELF_DISPATCH,
        skipGeocodeValidation: true,
        skipDispatchTransition: true,
        userName,
      });

      if (riderFromOrders && shipmentData?.shipmentId) {
        const { updateShipmentService } =
          await import("../../ShipmentsDashboard/service");

        const pickupTime = new Date().toISOString();

        let suggestedVehicleType;
        if (group.orderDataMap) {
          const selectedOrdersData = group.selectedOrders
            .map((orderNo) => group.orderDataMap[orderNo])
            .filter(Boolean);

          if (selectedOrdersData.length > 0) {
            try {
              suggestedVehicleType =
                calculateSuggestedVehicleType(selectedOrdersData);
            } catch (error) {
              console.error("Error calculating suggested vehicle type:", error);
            }
          }
        }

        await updateShipmentService(shipmentData.shipmentId, {
          deliveryTracking: {
            deliveryAgent: {
              name: riderFromOrders.name,
              phoneNumber: riderFromOrders.phone,
              vehicleDetails: {
                vehicleNumber: riderFromOrders.vehicleNumber,
                ...(suggestedVehicleType && {
                  vehicleType: suggestedVehicleType,
                }),
              },
            },
            timeline: {
              pickedUpAt: pickupTime,
            },
          },
          ...(riderFromOrders.trackingUrl?.trim() && {
            deliveryBooking: {
              trackingUrl: riderFromOrders.trackingUrl.trim(),
            },
          }),
        });
        // This is the shipday integration to push the order to shipday and assign a delivery partner, deferred for now.
        // if (riderFromOrders.phone === "919024083319") {
        //   const firstOrderNo = Object.keys(group.orderDataMap)[0];
        //   const orderData = group.orderDataMap[firstOrderNo];

        //   const pickupDate = new Date(pickupTime);
        //   const expectedDeliveryDate = pickupDate.toISOString().split("T")[0];
        //   const expectedPickupTime = pickupDate
        //     .toISOString()
        //     .split("T")[1]
        //     .split(".")[0];
        //   const deliveryDate = new Date(pickupDate.getTime() + 30 * 60 * 1000);
        //   const expectedDeliveryTime = deliveryDate
        //     .toISOString()
        //     .split("T")[1]
        //     .split(".")[0];
        //   const shipdayPayload = {
        //     orderNumber: group.selectedOrders[0],
        //     customerName: group.customerName,
        //     customerAddress: `${orderData.shipmentAddress?.addressLine1}, ${orderData.shipmentAddress?.city}, ${orderData.shipmentAddress?.state}`,
        //     customerPhoneNumber: "+917030067700",
        //     restaurantName: group.supplierName,
        //     restaurantAddress: `${group.selectedCustomerSupplier?.supplierAddress?.addressLine1}, ${group.selectedCustomerSupplier?.supplierAddress?.addressLine2}, ${group.selectedCustomerSupplier?.supplierAddress?.city}, ${group.selectedCustomerSupplier?.supplierAddress?.state}, ${group.selectedCustomerSupplier?.supplierAddress?.pinCode}, ${group.selectedCustomerSupplier?.supplierAddress?.country}`,
        //     restaurantPhoneNumber:
        //       shipmentData.pickupDetails?.address?.contactDetails?.phoneNumber,
        //     expectedDeliveryDate: expectedDeliveryDate,
        //     expectedPickupTime: expectedPickupTime,
        //     expectedDeliveryTime: expectedDeliveryTime,
        //     pickupLatitude: shipmentData.pickupDetails?.address?.lat,
        //     pickupLongitude: shipmentData.pickupDetails?.address?.lng,
        //     deliveryLatitude: shipmentData.dropDetails?.address?.lat,
        //     deliveryLongitude: shipmentData.dropDetails?.address?.lng,
        //     tips: 0,
        //     tax: 0,
        //     discountAmount: 0,
        //     deliveryFee: 0,
        //     totalOrderCost:
        //       orderData.invoiceInfo?.invoiceAmount ||
        //       orderData.challanInfo?.challanAmount,
        //     deliveryInstruction: `Invoice/Challan Number :${
        //       orderData.invoiceInfo?.invoiceNo ||
        //       orderData.challanInfo?.challanNo
        //     }`,
        //     paymentMethod: "cash",
        //   };
        //   try {
        //     const createOrderResponse = await fetch(
        //       "https://api.shipday.com/orders",
        //       {
        //         method: "POST",
        //         headers: {
        //           "Content-Type": "application/json",
        //           Authorization: "Basic 3ni3H6KCNV.FX1S9mXd9rwJMxN3xWEg",
        //         },
        //         body: JSON.stringify(shipdayPayload),
        //       }
        //     );

        //     const createOrderData = await createOrderResponse.json();
        //     const createdDeliveryOrderId = createOrderData.orderId;
        //     await fetch(
        //       `https://api.shipday.com/orders/assign/${createdDeliveryOrderId}/449975`,
        //       {
        //         method: "PUT",
        //         headers: {
        //           Authorization: "Basic 3ni3H6KCNV.FX1S9mXd9rwJMxN3xWEg",
        //         },
        //       }
        //     );
        //   } catch (error) {
        //     console.error("Error calling Shipday API:", error);
        //   }
        // }
      }

      toast({
        title: "Shipment Created Successfully",
        description: (
          <Box>
            <Text fontSize="sm">Shipment ID: {shipmentData?.shipmentId}</Text>
            <Text fontSize="sm">Orders: {group.selectedOrders.join(", ")}</Text>
          </Box>
        ),
        status: "success",
        duration: 8000,
        isClosable: true,
        position: "top",
      });

      clearGroup(groupKey);
      await refetchAllStats();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const onSelfPickup = async (
    groupKey: string,
    picker?: SelectedRiderDetails,
  ) => {
    const group = selectionsByGroup[groupKey];
    if (!group.selectedOrders.length) {
      toast({
        title: "No Orders Selected",
        description: "Please select orders to process self pickup",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      const modeFromGroup = getGroupDeliveryMode(groupKey);
      const deliveryMode = modeFromGroup || DeliveryModes.SELF_PICKUP;
      const shipmentData = await createShipmentForOrders({
        orderNumbers: group.selectedOrders,
        orderIds: group.orderIdMap || {},
        deliveryMode,
        dispatchMode: DispatchMode.SELF_PICKUP,
        skipDispatchTransition: true,
        skipGeocodeValidation: true,
      });

      if (shipmentData?.shipmentId) {
        const { updateShipmentService } =
          await import("../../ShipmentsDashboard/service");

        const currentTime = new Date().toISOString();

        await updateShipmentService(shipmentData.shipmentId, {
          status: "DELIVERED",
          deliveryTracking: {
            deliveryAgent: {
              name: picker?.name || "Self Pickup",
              phoneNumber: picker?.phone || "",
            },
            timeline: {
              pickedUpAt: currentTime,
              deliveredAt: currentTime,
            },
          },
        });
      }

      toast({
        title: "Self Pickup Processed Successfully",
        description: (
          <Box>
            <Text fontSize="sm">Shipment ID: {shipmentData?.shipmentId}</Text>
            <Text fontSize="sm">Orders: {group.selectedOrders.join(", ")}</Text>
            {picker && (
              <Text fontSize="sm">
                Picker Details: {picker.name} ({picker.phone})
              </Text>
            )}
          </Box>
        ),
        status: "success",
        duration: 8000,
        isClosable: true,
        position: "top",
      });

      clearGroup(groupKey);
      await refetchAllStats();
    } catch (error: any) {
      toast({
        title: "Error Processing Self Pickup",
        description: error?.message || "Failed to process self pickup",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const getGroupDeliveryMode = useCallback(
    (groupKey: string): DeliveryModes | undefined => {
      const group = selectionsByGroup[groupKey];
      if (!group) return undefined;
      const modes = group.selectedOrders
        .map((o) => group.orderDeliveryModes?.[o])
        .filter((m): m is DeliveryModes => m !== undefined && m !== null);
      if (modes.length === 0) return undefined;
      const unique = Array.from(new Set(modes));
      return unique.length === 1 ? unique[0] : undefined;
    },
    [selectionsByGroup],
  );

  const toggleCustomerExpansion = useCallback((customerId: string) => {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      next.has(customerId) ? next.delete(customerId) : next.add(customerId);
      return next;
    });
  }, []);

  const toggleSupplierExpansion = useCallback((supplierId: string) => {
    setExpandedSuppliers((prev) => {
      const next = new Set(prev);
      next.has(supplierId) ? next.delete(supplierId) : next.add(supplierId);
      return next;
    });
  }, []);

  const toggleRiderExpansion = useCallback((riderId: string) => {
    setExpandedRiders((prev) => {
      const next = new Set(prev);
      next.has(riderId) ? next.delete(riderId) : next.add(riderId);
      return next;
    });
  }, []);

  const groupingLabel =
    grouping === GroupingMode.CUSTOMER
      ? GroupingMode.CUSTOMER
      : grouping === GroupingMode.RIDER
      ? GroupingMode.RIDER
      : GroupingMode.SUPPLIER;

  const groupingIcon = isCustomerGrouping
    ? FaUsers
    : isRiderGrouping
    ? FaMotorcycle
    : FaStore;

  const handleOrderSelectionChange = useCallback(
    async (
      orderNo: string,
      isSelected: boolean,
      customerId: string,
      customerName: string,
      supplierId?: string,
      supplierName?: string,
      deliveryMode?: DeliveryModes,
      orderId?: string,
      orderData?: any,
    ) => {
      if (isSelected) {
        addSelectedOrder(
          orderNo,
          customerId,
          customerName,
          supplierId,
          supplierName,
          deliveryMode,
          orderId,
          orderData,
        );
      } else {
        removeSelectedOrder(
          orderNo,
          customerId,
          customerName,
          supplierId,
          supplierName,
        );
      }
    },
    [addSelectedOrder, removeSelectedOrder],
  );

  const ensureSupplierForGroup = useCallback(
    async (groupKey: string) => {
      const group = selectionsByGroup[groupKey];
      if (!group) return null;

      const customerId = group.customerId;
      if (!customerId) return null;

      if (group.selectedCustomerSupplier) {
        return group.selectedCustomerSupplier;
      }

      try {
        const targetSupplier = await findSupplierByName(
          customerId,
          group.supplierName,
        );
        updateGroupQuote(groupKey, {
          selectedCustomerSupplier: targetSupplier,
        });
        return targetSupplier;
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        return null;
      }
    },
    [selectionsByGroup, findSupplierByName, toast, updateGroupQuote],
  );

  const fetchPorterQuoteForGroup = useCallback(
    async (groupKey: string) => {
      const group = selectionsByGroup[groupKey];
      if (!group || group.selectedOrders.length === 0) return;

      // Porter quotes are only needed on R2D dashboard, not Dispatched dashboard
      if (isDispatchedDashboard) return;

      if (isConsolidatedSelectionGroup(groupKey, group)) {
        return;
      }

      const mode = getGroupDeliveryMode(groupKey);
      if (mode !== DeliveryModes.DOOR_DELIVERY) {
        return;
      }

      const customerId = group.customerId;
      if (!customerId) return null;

      setGroupLoading(groupKey, true);

      try {
        const supplier = await ensureSupplierForGroup(groupKey);
        if (!supplier) {
          setGroupLoading(groupKey, false);
          return;
        }

        const customerRecord = getCustomerRecordById(customerId);
        const individualOrderResponse = await getDispatchOrdersForSupplier(
          customerId,
          supplier._id,
        );
        const firstOrder = individualOrderResponse?.orders?.[0];
        const orderFromSelection = group.orderDataMap?.[group.selectedOrders[0]];

        const fallbackShipmentAddress =
          firstOrder?.shipmentAddress ||
          orderFromSelection?.shipmentAddress ||
          customerRecord?.facet?.shipmentAddress;

        const updatedCustomerRecord = {
          ...(customerRecord || {
            customerId,
            customerName: group.customerName,
          }),
          facet: {
            ...(customerRecord?.facet || {}),
            shipmentAddress: fallbackShipmentAddress,
            mobileNumber:
              customerRecord?.facet?.mobileNumber ||
              orderFromSelection?.customer?.mobileNumber,
          },
        };

        const payload = createPorterQuotePayload(
          supplier,
          updatedCustomerRecord,
          group.customerName,
        );
        const response = await fetchPorterQuote(payload);
        const orderHash = group.selectedOrders.slice().sort().join("|");

        updateGroupQuote(groupKey, {
          porterQuote: response,
          porterQuoteLoading: false,
          lastQuoteOrderHash: orderHash,
        });

        setGroupLoading(groupKey, false);
      } catch (error: any) {
        const orderHash = group.selectedOrders.slice().sort().join("|");
        updateGroupQuote(groupKey, {
          porterQuoteLoading: false,
          lastQuoteOrderHash: orderHash,
        });

        toast({
          title: "Error",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    },
    [
      selectionsByGroup,
      getGroupDeliveryMode,
      ensureSupplierForGroup,
      createPorterQuotePayload,
      fetchPorterQuote,
      toast,
      updateGroupQuote,
      setGroupLoading,
      getCustomerRecordById,
    ],
  );

  useEffect(() => {
    Object.entries(selectionsByGroup).forEach(([key, group]) => {
      const orderHash = group.selectedOrders.slice().sort().join("|");
      const mode = getGroupDeliveryMode(key);
      const shouldFetchPorterQuote =
        !isDispatchedDashboard &&
        activeGroupKeys.has(key) &&
        !isConsolidatedSelectionGroup(key, group) &&
        group.selectedOrders.length > 0 &&
        orderHash !== group.lastQuoteOrderHash &&
        !group.porterQuoteLoading &&
        mode === DeliveryModes.DOOR_DELIVERY;

      if (shouldFetchPorterQuote) {
        fetchPorterQuoteForGroup(key);
      } else if (mode !== DeliveryModes.DOOR_DELIVERY) {
        if (group.porterQuote || group.porterQuoteLoading) {
          updateGroupQuote(key, {
            porterQuote: undefined,
            porterQuoteLoading: false,
          });
        }
      }
    });
  }, [
    selectionsByGroup,
    getGroupDeliveryMode,
    fetchPorterQuoteForGroup,
    activeGroupKeys,
  ]);

  const stats = useMemo(() => getTotalStats(), [groupedCustomers]);

  const fetchTotalStats = useCallback(async () => {
    if (!isApiConfigReady || totalStatsFetchingRef.current) return;

    try {
      totalStatsFetchingRef.current = true;
      setTotalStatsLoading(true);
      const totalFacets = await getDispatchCustomerFacets({}, undefined);
      const totalCustomers = totalFacets?.customers || [];
      const calculatedStats = calculateStatsFromCustomers(totalCustomers);
      setTotalStats(calculatedStats);
    } catch (error) {
      console.error("Error fetching total stats:", error);
      setTotalStats({
        totalOrders: 0,
        totalCustomers: 0,
        totalValues: 0,
        urgentOrders: 0,
      });
    } finally {
      setTotalStatsLoading(false);
      totalStatsFetchingRef.current = false;
    }
  }, [isApiConfigReady]);

  const fetchHLDStats = useCallback(async () => {
    if (!isApiConfigReady || !enableDistanceTabs || hldStatsFetchingRef.current)
      return;

    try {
      hldStatsFetchingRef.current = true;
      setHldStatsLoading(true);
      const hldFacets = await getDispatchCustomerFacets({}, FASTEST_CPT_SLAB_LEVEL);
      const hldCustomers = hldFacets?.customers || [];
      const calculatedStats = calculateStatsFromCustomers(hldCustomers);
      setHldStats(calculatedStats);
    } catch (error) {
      console.error("Error fetching HLD stats:", error);
      setHldStats({
        totalOrders: 0,
        totalCustomers: 0,
        totalValues: 0,
        urgentOrders: 0,
      });
    } finally {
      setHldStatsLoading(false);
      hldStatsFetchingRef.current = false;
    }
  }, [isApiConfigReady, enableDistanceTabs]);

  const refetchAllStats = useCallback(async () => {
    if (!isApiConfigReady) return;

    setIsRefreshingAll(true);
    try {
      const promises: Promise<void>[] = [];

      totalStatsFetchingRef.current = false;
      hldStatsFetchingRef.current = false;

      if (enableDistanceTabs) {
        promises.push(fetchTotalStats());
        promises.push(fetchHLDStats());
      }

      promises.push(
        refetch()
          .then(() => undefined)
          .catch((error: any) => {
            console.error("Error refetching customer facets:", error);
          }),
      );

      await Promise.allSettled(promises);
    } catch (error) {
      console.error("Error refetching all stats:", error);
    } finally {
      setIsRefreshingAll(false);
    }
  }, [
    isApiConfigReady,
    enableDistanceTabs,
    fetchTotalStats,
    fetchHLDStats,
    refetch,
  ]);

  useEffect(() => {
    if (refetchAllStats) {
      onRefetchReady?.(refetchAllStats);
    }
  }, [refetchAllStats, onRefetchReady]);

  useEffect(() => {
    if (!enableDistanceTabs) {
      setTotalStats(null);
      return;
    }
    fetchTotalStats();
  }, [enableDistanceTabs, fetchTotalStats]);

  useEffect(() => {
    if (!enableDistanceTabs) {
      setHldStats(null);
      return;
    }
    fetchHLDStats();
  }, [enableDistanceTabs, fetchHLDStats]);

  useEffect(() => {
    let finalStats;
    if (enableDistanceTabs) {
      if (!totalStats) return;
      finalStats = {
        ...totalStats,
        selectedOrders: selectedOrders.length,
        hldOrders: hldStats?.totalOrders ?? 0,
      };
    } else {
      finalStats = {
        ...stats,
        selectedOrders: selectedOrders.length,
        hldOrders: hldStats?.totalOrders ?? 0,
      };
    }

    onStatsUpdate?.(finalStats);
  }, [
    stats,
    totalStats,
    selectedOrders,
    onStatsUpdate,
    hldStats,
    enableDistanceTabs,
  ]);

  if (loading) {
    return (
      <Center py={20}>
        <VStack spacing={6}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <VStack spacing={2}>
            <Heading size="md" color="gray.700">
              Loading Orders
            </Heading>
            <Text color="gray.500" fontSize="sm">
              Fetching {groupingLabel} data and order information...
            </Text>
          </VStack>
        </VStack>
      </Center>
    );
  }

  if (!facets) {
    return (
      <Center py={20}>
        <VStack spacing={6} maxW="md" textAlign="center">
          <Box p={6} bg="blue.50" borderRadius="full">
            <Icon as={FaExclamationTriangle} boxSize={8} color="blue.500" />
          </Box>
          <VStack spacing={3}>
            <Heading size="lg" color="gray.700">
              No Data Available
            </Heading>
            <Text color="gray.500" fontSize="sm">
              No {groupingLabel} facets found. Please check your connection and
              try again.
            </Text>
          </VStack>
          <Button
            onClick={() => refetch()}
            colorScheme="blue"
            leftIcon={<Icon as={groupingIcon} />}
            size="lg"
            px={8}
            cursor="pointer"
          >
            Load {groupingLabel}
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      <QuoteBar
        activeGroups={activeGroups}
        selectedOrders={selectedOrders}
        quoteCardsExpanded={quoteCardsExpanded}
        allowMultiCustomerShipment={allowMultiCustomerShipment}
        onToggleExpansion={() => setQuoteCardsExpanded(!quoteCardsExpanded)}
        onCloseGroup={clearGroup}
        riders={riders}
        ridersLoading={ridersLoading}
        onBookPorter={(groupKey) => onBookPorter(groupKey)}
        onSelfDispatch={(groupKey, rider) => onSelfDispatch(groupKey, rider)}
        onSelfPickup={(groupKey, picker) => onSelfPickup(groupKey, picker)}
        selfDispatchButtonLabel={
          isDispatchedDashboard ? "Create Shipment" : "Self Dispatch"
        }
        skipRiderDialog={isDispatchedDashboard}
        allowBookPorter={!isDispatchedDashboard}
      />

      <VStack spacing={6} align="stretch">
        {isCustomerGrouping
          ? Object.entries(groupedCustomers).map(
              ([customerId, customerData]) => (
                <GroupCard
                  key={customerId}
                  entityId={customerId}
                  entityName={customerData.customerName}
                  groupingMode={grouping}
                  expanded={expandedCustomers.has(customerId)}
                  onToggleExpansion={() => toggleCustomerExpansion(customerId)}
                  entityFacet={customerData.facet}
                  isLoading={false}
                  selectedOrders={selectedOrders}
                  activeTab={enableDistanceTabs ? activeTab : undefined}
                  collapseSignal={collapseSignal}
                  showRiderDetail={isDispatchedDashboard}
                  onOrderSelectionChange={(
                    orderNo: string,
                    isSelected: boolean,
                    customerId?: string,
                    customerName?: string,
                    supplierId?: string,
                    supplierName?: string,
                    deliveryMode?: DeliveryModes,
                    orderId?: string,
                    orderData?: any,
                  ) =>
                    handleOrderSelectionChange(
                      orderNo,
                      isSelected,
                      customerId || "",
                      customerName || "",
                      supplierId,
                      supplierName,
                      deliveryMode,
                      orderId,
                      orderData,
                    )
                  }
                />
              ),
            )
          : isRiderGrouping
          ? Object.entries(groupedRiders).map(([riderId, riderData]) => (
              <GroupCard
                key={riderId}
                entityId={riderId}
                entityName={riderData.riderName}
                groupingMode={grouping}
                expanded={expandedRiders.has(riderId)}
                onToggleExpansion={() => toggleRiderExpansion(riderId)}
                entityFacet={riderData.facet}
                isLoading={false}
                selectedOrders={selectedOrders}
                activeTab={enableDistanceTabs ? activeTab : undefined}
                collapseSignal={collapseSignal}
                showRiderDetail={isDispatchedDashboard}
                onOrderSelectionChange={(
                  orderNo: string,
                  isSelected: boolean,
                  customerId?: string,
                  customerName?: string,
                  supplierId?: string,
                  supplierName?: string,
                  deliveryMode?: DeliveryModes,
                  orderId?: string,
                  orderData?: any,
                ) =>
                  handleOrderSelectionChange(
                    orderNo,
                    isSelected,
                    customerId || "",
                    customerName || "",
                    supplierId,
                    supplierName,
                    deliveryMode,
                    orderId,
                    orderData,
                  )
                }
              />
            ))
          : Object.entries(groupedSuppliers).map(
              ([supplierId, supplierData]) => (
                <GroupCard
                  key={supplierId}
                  entityId={supplierId}
                  entityName={supplierData.supplierName}
                  groupingMode={grouping}
                  expanded={expandedSuppliers.has(supplierId)}
                  onToggleExpansion={() => toggleSupplierExpansion(supplierId)}
                  entityFacet={supplierData.facet}
                  isLoading={false}
                  selectedOrders={selectedOrders}
                  activeTab={enableDistanceTabs ? activeTab : undefined}
                  collapseSignal={collapseSignal}
                  showRiderDetail={isDispatchedDashboard}
                  onOrderSelectionChange={(
                    orderNo: string,
                    isSelected: boolean,
                    customerId?: string,
                    customerName?: string,
                    supplierId?: string,
                    supplierName?: string,
                    deliveryMode?: DeliveryModes,
                    orderId?: string,
                    orderData?: any,
                  ) =>
                    handleOrderSelectionChange(
                      orderNo,
                      isSelected,
                      customerId || "",
                      customerName || "",
                      supplierId,
                      supplierName,
                      deliveryMode,
                      orderId,
                      orderData,
                    )
                  }
                />
              ),
            )}
      </VStack>
    </>
  );
}
